<template>
  <v-app>
    <v-main>
      <AppLayout>
        <router-view :key="$route.path" />
      </AppLayout>

      <BaseScoreDialog v-if="shouldScoreRender" />
      <BaseScoreDialog v-if="shouldScoreLdpRender" />
      <BaseSnackbar />
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { reportForcedLogout } from "@/utils/forcedLogout";
import AppLayout from "@/layouts/AppLayout";
import getMetaInfo from "@/lib/meta";
import IS_EXPIRED from "@/graphql/query/isExpiredConnection.gql";
import BaseSnackbar from "@/components/Base/Snackbar";
import BaseScoreDialog from "@/components/Base/ScoreDialog";
import { isEmbedPath } from "@/utils/embedPath";

export default {
  name: "App",
  components: {
    AppLayout,
    BaseSnackbar,
    BaseScoreDialog,
  },
  data() {
    return {
      isExpired: null,
    };
  },
  async created() {
    // DL-452: on the embed routes there is no legacy session to restore. Without this guard
    // getUser returns "Error de Autenticación" and the errorLink in vue-apollo.js bounces the
    // iframe to /acceder. It uses the pathname rather than `this.$route.meta` because created()
    // runs before the router resolves its first navigation (the global beforeEach is async), so
    // `meta` is still empty there.
    if (isEmbedPath()) return;

    try {
      await this.getUser({ apolloClient: this.$apollo });
      const userId = this.user?._id || null;
      const userRole = this.user?._type.active || null;
      const userOrganization = this.user?.organization?._id || null;
      const userProgram = this.user?.programs?.length
        ? this.user.programs[this.user.programs.length - 1]._id
        : null;
      if (userId && userRole) {
        window.dataLayer.push({
          event: "user_identified",
          user_id: userId,
          role: userRole,
          organization: userOrganization,
          program: userProgram,
        });
      }
    } catch (error) {
      console.error("User not identified", error);
    }
    this.updateFreshworksLocale(this.$i18n.locale);
  },
  computed: {
    ...mapGetters("users", [
      "isLoggedIn",
      "user",
      "isCoach",
      "activeRole",
      "userGroup",
    ]),
    ...mapGetters("schedules", ["getSessionList"]),
    ...mapGetters("chat", ["getUnreadConversations"]),

    shouldScoreRender() {
      return (
        (this.userGroup === "collaborator" && this.activeRole === "coach") ||
        (this.userGroup === "customer" && this.activeRole === "coachee")
      );
    },
    shouldScoreLdpRender() {
      return this.userGroup === "customer" && this.activeRole === "admin";
    },
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.updateFreshworksLocale(newLocale);
    },
    isLoggedIn() {
      // DL-452: the created() guard does not cover the watchers. If anything put a user in the
      // store during the iframe's lifetime, this would start an addSmartQuery with a pollInterval:
      // authenticated GraphQL in a loop inside the embed.
      if (isEmbedPath()) return;

      if (!this.isLoggedIn) {
        if (!!this.$apollo.queries.isExpired)
          this.$apollo.queries.isExpired.stopPolling();
      } else {
        this.$apollo.addSmartQuery("isExpired", {
          pollInterval: process.env.VUE_APP_POLLINTERVAL_TIME,
          query: IS_EXPIRED,
          update: (data) => data.User.expireConnection,
        });
      }
    },
    isExpired() {
      // DL-452: signout() + location.reload() inside an iframe reloads the embed and logs the
      // user out of legacy in their other tab. That is the DL-1408 failure class, and it must not
      // be reachable from a public route.
      if (isEmbedPath()) return;

      if (this.isExpired) {
        if (!!this.$apollo.queries.isExpired)
          this.$apollo.queries.isExpired.stopPolling();
        reportForcedLogout({
          trigger: "polling_isExpired",
          page: this.$route?.name,
          userId: this.user?._id,
        });
        this.signout();
        location.reload();
      }
    },
    user() {
      if (this.user) this.initializeUserConversations();
    },
  },
  metaInfo() {
    return {
      ...getMetaInfo(),
    };
  },
  async beforeMount() {
    // DL-452: same reason as created(). Besides getForms (one GraphQL request per iframe load),
    // this hook's catch does $router.push({name:"Signout"}) when the error is
    // "Error de Autenticación": an escape hatch the errorLink gate cannot see, because App.vue
    // does the push itself, not Apollo.
    if (isEmbedPath()) return;

    try {
      const forms = await this.getForms({ apolloClient: this.$apollo });
      const token = localStorage.getItem("token");
      if (!this.isLoggedIn) {
        if (!!this.$apollo.queries.isExpired)
          this.$apollo.queries.isExpired.stopPolling();
      } else {
        this.$apollo.addSmartQuery("isExpired", {
          pollInterval: process.env.VUE_APP_POLLINTERVAL_TIME,
          query: IS_EXPIRED,
          update: (data) => data.User.expireConnection,
        });
        await this.fetchSessionList({
          apolloClient: this.$apollo,
          data: {},
        });

        await this.readyToTalk();
      }
    } catch (error) {
      console.error(error);
      if (error.length > 0 && error[0] === "Error de Autenticación") {
        this.$router.push({ name: "Signout" });
      }
    }
  },
  methods: {
    ...mapActions("schedules", ["fetchSessionList"]),
    ...mapActions("contents", ["getForms"]),
    ...mapActions("users", ["signout", "getUser"]),
    ...mapActions("chat", [
      "readyToTalk",
      "createSession",
      "createUser",
      "createConversation",
      "setUnread",
    ]),
    updateFreshworksLocale(locale) {
      const LOCALE_MAP = { "pt-br": "pt-BR" };
      const freshLocale = LOCALE_MAP[locale] ?? locale;
      window.FreshworksWidget?.("setLocale", freshLocale);
      window.FreshworksWidget?.("setLabels", {
        es: {
          banner: "Bienvenida al servicio de soporte Yourney",
          launcher: "Soporte",
          contact_form: { title: "Contáctanos" },
        },
        en: {
          banner: "Welcome to Yourney support",
          launcher: "Support",
          contact_form: { title: "Contact Us" },
        },
        "pt-BR": {
          banner: "Boas-vindas ao suporte Yourney",
          launcher: "Suporte",
          contact_form: { title: "Fale conosco" },
        },
      });
    },
    async initializeUserConversations() {
      try {
        this.me = await this.createUser(this.user);
        this.chatSession = await this.createSession({ me: this.me });
        this.inbox = this.chatSession.createChatbox();
        this.chatSession.unreads.onChange((unreadConversations) => {
          this.unreadConversation = [...unreadConversations];
          this.setUnread([...unreadConversations]);
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss">
.v-application p {
  margin-bottom: 0;
}
.main-app {
  max-width: 1220px !important;
  justify-content: flex-start;
}
</style>
